// Assets + Training + 1:1 Notes + Goals all together for build velocity.
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { fmtDate, fmtDay, fmtMoney, todayISO } from "@/lib/utils";
import { plainError } from "@/lib/plain-error";
import { VideoPlayer } from "@/components/VideoPlayer";

interface Person { id: string; first_name: string | null; last_name: string | null; email: string; }

// ── TRAINING ──────────────────────────────────────────────────────────────
interface TModule {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  document_url: string | null;
  video_url: string | null;
  video_storage_path: string | null;
  content: string | null;
  estimated_minutes: number | null;
  required_for_roles: string[];
  recurrence_months: number | null;
  due_within_days: number | null;
  sort_order: number | null;
  prerequisite_module_id: string | null;
  active: boolean;
}
interface TStatusRow {
  employee_id: string;
  role: string | null;
  module_id: string;
  title: string;
  category: string | null;
  due_within_days: number | null;
  recurrence_months: number | null;
  completed_at: string | null;
  expires_at: string | null;
  // HQ-TRAIN-001. "optional" is an elective — visible to everyone, owed by
  // nobody. It used to arrive as "pending" and be counted as required.
  status: "completed" | "expired" | "overdue" | "pending_due_soon" | "pending" | "optional";
  days_until_due: number | null;
  required: boolean;
}

const STATUS_ORDER: Record<TStatusRow["status"], number> = {
  overdue: 0, expired: 1, pending_due_soon: 2, pending: 3, optional: 4, completed: 5,
};

function statusPill(s: TStatusRow["status"]) {
  switch (s) {
    case "overdue": return { cls: "pill pill-bad", label: "Overdue" };
    case "expired": return { cls: "pill pill-bad", label: "Expired" };
    case "pending_due_soon": return { cls: "pill pill-warn", label: "Due soon" };
    case "pending": return { cls: "pill pill-mint", label: "Pending" };
    case "optional": return { cls: "pill pill-mint", label: "Optional" };
    case "completed": return { cls: "pill pill-ok", label: "Completed" };
  }
}

export function Training() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<TStatusRow[]>([]);
  const [mods, setMods] = useState<TModule[]>([]);
  const [openMod, setOpenMod] = useState<TModule | null>(null);
  const [trainError, setTrainError] = useState<string | null>(null);

  useEffect(() => { if (employee) void load(); }, [employee?.id]);
  async function load() {
    if (!employee) return;
    const [{ data: s }, { data: m }] = await Promise.all([
      supabase.from("hq_training_status").select("*").eq("employee_id", employee.id),
      supabase.from("hq_training_modules").select("*").eq("active", true),
    ]);
    setRows((s as TStatusRow[]) ?? []);
    setMods((m as TModule[]) ?? []);
  }
  // HQ-TRAIN-002. The prerequisite was never consulted here, and the insert's
  // error was discarded, so completing a module out of order looked exactly
  // like completing it in order. The database refuses it now
  // (sql/2027-01-03_training_prerequisites.sql); this shows why.
  async function markDone(modId: string) {
    if (!employee) return;
    setTrainError(null);
    const mod = mods.find(x => x.id === modId);
    const expires = mod?.recurrence_months
      ? todayISO(new Date(Date.now() + mod.recurrence_months * 30 * 86400000)) : null;
    const { error } = await supabase.from("hq_training_completions")
      .insert({ module_id: modId, employee_id: employee.id, expires_at: expires });
    if (error) { setTrainError(plainError(error, "record that completion")); return; }
    setOpenMod(null);
    await load();
  }

  /** The prerequisite this person still owes for a module, if any. */
  function unmetPrereq(mod: TModule | null): TModule | null {
    if (!mod?.prerequisite_module_id) return null;
    const done = rows.some(r =>
      r.module_id === mod.prerequisite_module_id && r.status === "completed");
    return done ? null : (modById[mod.prerequisite_module_id] ?? null);
  }

  const modById = useMemo(() => Object.fromEntries(mods.map(m => [m.id, m])), [mods]);

  const bySeverity = (a: TStatusRow, b: TStatusRow) => {
    const so = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    return so !== 0 ? so : a.title.localeCompare(b.title);
  };

  // HQ-TRAIN-001. The admin screen says a module with no required roles is
  // elective. This page then listed it under "Required for your role" and
  // counted it in "N required". Two separate lists now, and the count is only
  // of what is actually owed.
  const pending = useMemo(
    () => rows.filter(r => r.required && r.status !== "completed").sort(bySeverity), [rows]);
  const optional = useMemo(
    () => rows.filter(r => !r.required && r.status !== "completed").sort(bySeverity), [rows]);
  const completed = useMemo(() => rows.filter(r => r.status === "completed"), [rows]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Training 📚</h1>
      <p className="text-sm text-gray2">
        {pending.length} required for your role
        {optional.length > 0 && <> · {optional.length} available to you</>}
        {" "}· {completed.length} completed
      </p>

      {pending.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray2">Required for your role</h2>
          {pending.map(r => {
            const mod = modById[r.module_id];
            const pill = statusPill(r.status);
            const showDot = r.status === "overdue" || r.status === "expired";
            const hasVideo = !!(mod?.video_url || mod?.video_storage_path);
            return (
              <button
                key={r.module_id}
                onClick={() => mod && setOpenMod(mod)}
                className="card w-full text-left transition hover:ring-teal"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {showDot && <span className="inline-block h-2 w-2 rounded-full bg-bad" />}
                      {r.category && <span className="pill pill-mint">{r.category}</span>}
                      {hasVideo && <span className="pill pill-mint">📹 Video</span>}
                      {mod?.estimated_minutes != null && <span className="pill pill-mint">{mod.estimated_minutes} min</span>}
                    </div>
                    <h3 className="mt-1 font-semibold">{r.title}</h3>
                    {r.days_until_due != null && (
                      <div className="mt-1 text-xs text-gray2">
                        {r.days_until_due < 0
                          ? `${Math.abs(r.days_until_due)} days overdue`
                          : r.days_until_due === 0
                            ? "Due today"
                            : `Due in ${r.days_until_due} day${r.days_until_due === 1 ? "" : "s"}`}
                      </div>
                    )}
                  </div>
                  <span className={pill.cls}>{pill.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {optional.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray2">Available to you</h2>
          <p className="text-xs text-gray2">Nobody is waiting on these. Take them if they are useful.</p>
          {optional.map(r => {
            const mod = modById[r.module_id];
            const hasVideo = !!(mod?.video_url || mod?.video_storage_path);
            return (
              <button
                key={r.module_id}
                onClick={() => mod && setOpenMod(mod)}
                className="card w-full text-left transition hover:ring-teal"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {r.category && <span className="pill pill-mint">{r.category}</span>}
                      {hasVideo && <span className="pill pill-mint">📹 Video</span>}
                      {mod?.estimated_minutes != null && <span className="pill pill-mint">{mod.estimated_minutes} min</span>}
                    </div>
                    <h3 className="mt-1 font-semibold">{r.title}</h3>
                  </div>
                  <span className="pill pill-mint">Optional</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {completed.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray2">Completed</h2>
          {completed.map(r => {
            const mod = modById[r.module_id];
            return (
              <button
                key={r.module_id}
                onClick={() => mod && setOpenMod(mod)}
                className="card w-full text-left opacity-80 transition hover:opacity-100 hover:ring-teal"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    {r.category && <span className="pill pill-mint">{r.category}</span>}
                    <h3 className="mt-1 font-semibold">{r.title}</h3>
                    {r.completed_at && (
                      <div className="text-xs text-gray2 mt-1">
                        Completed {fmtDate(r.completed_at)}
                        {r.expires_at && <> · expires {fmtDay(r.expires_at)}</>}
                      </div>
                    )}
                  </div>
                  <span className="pill pill-ok">Completed</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {pending.length === 0 && optional.length === 0 && completed.length === 0 && (
        <div className="card py-10 text-center text-sm text-gray2">No training required for your role.</div>
      )}
      {pending.length === 0 && (optional.length > 0 || completed.length > 0) && (
        <div className="card py-6 text-center text-sm text-ok">Nothing required of you right now. ✓</div>
      )}

      {openMod && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setOpenMod(null)}>
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-1">
                  {openMod.category && <span className="pill pill-mint">{openMod.category}</span>}
                  {openMod.estimated_minutes != null && (
                    <span className="pill pill-mint">📹 {openMod.estimated_minutes} min</span>
                  )}
                </div>
                <h2 className="text-xl font-bold">{openMod.title}</h2>
              </div>
              <button onClick={() => setOpenMod(null)} className="text-2xl text-gray2 hover:text-ink">×</button>
            </div>
            {(openMod.video_url || openMod.video_storage_path) && (
              <div className="mb-4">
                <VideoPlayer url={openMod.video_url} storagePath={openMod.video_storage_path} />
              </div>
            )}
            {openMod.description && <p className="mb-4 text-sm text-gray2">{openMod.description}</p>}
            {trainError && (
              <div className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{trainError}</div>
            )}
            {openMod.document_url && (
              <a href={openMod.document_url} target="_blank" rel="noreferrer" className="btn mb-4 inline-block">↗ Open document</a>
            )}
            {openMod.content && (
              <article className="prose prose-sm max-w-none whitespace-pre-wrap text-sm">{openMod.content}</article>
            )}
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-line">
              <button onClick={() => setOpenMod(null)} className="btn btn-ghost">Close</button>
              {/* Locked rather than offered-and-refused: the module it depends
                  on is named, and opening it is one click. */}
              {unmetPrereq(openMod) ? (
                <button
                  onClick={() => setOpenMod(unmetPrereq(openMod))}
                  className="btn btn-secondary"
                  title="This module depends on another one"
                >🔒 Do "{unmetPrereq(openMod)!.title}" first</button>
              ) : (
                <button onClick={() => markDone(openMod.id)} className="btn">Mark complete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TrainingAdmin() {
  const [rows, setRows] = useState<TModule[]>([]);
  const [editing, setEditing] = useState<Partial<TModule> | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { void load(); }, []);
  async function load() {
    const { data } = await supabase.from("hq_training_modules").select("*").order("category").order("sort_order").order("title");
    setRows((data as TModule[]) ?? []);
  }
  async function save() {
    if (!editing?.title) return;
    const payload = {
      title: editing.title, description: editing.description ?? null, category: editing.category ?? null,
      document_url: editing.document_url ?? null,
      video_url: editing.video_url ?? null,
      video_storage_path: editing.video_storage_path ?? null,
      content: editing.content ?? null,
      estimated_minutes: editing.estimated_minutes ?? null,
      required_for_roles: editing.required_for_roles ?? [],
      recurrence_months: editing.recurrence_months ?? null,
      due_within_days: editing.due_within_days ?? null,
      sort_order: editing.sort_order ?? null,
      prerequisite_module_id: editing.prerequisite_module_id ?? null,
      active: editing.active ?? true,
    };
    if (editing.id) await supabase.from("hq_training_modules").update(payload).eq("id", editing.id);
    else await supabase.from("hq_training_modules").insert(payload);
    setEditing(null); await load();
  }

  async function ensureModId(): Promise<string | null> {
    if (!editing) return null;
    if (editing.id) return editing.id;
    if (!editing.title) {
      setUploadError("Please enter a title before uploading a video.");
      return null;
    }
    const draft = {
      title: editing.title,
      description: editing.description ?? null,
      category: editing.category ?? null,
      document_url: editing.document_url ?? null,
      video_url: editing.video_url ?? null,
      video_storage_path: editing.video_storage_path ?? null,
      content: editing.content ?? null,
      estimated_minutes: editing.estimated_minutes ?? null,
      required_for_roles: editing.required_for_roles ?? [],
      recurrence_months: editing.recurrence_months ?? null,
      due_within_days: editing.due_within_days ?? null,
      sort_order: editing.sort_order ?? null,
      prerequisite_module_id: editing.prerequisite_module_id ?? null,
      active: editing.active ?? true,
    };
    const { data, error } = await supabase.from("hq_training_modules").insert(draft).select().single();
    if (error || !data) {
      setUploadError(error?.message ?? "Could not create module draft.");
      return null;
    }
    const newId = (data as TModule).id;
    setEditing(e => (e ? { ...e, id: newId } : e));
    return newId;
  }

  async function handleVideoUpload(file: File) {
    setUploadError(null);
    if (file.size > 500 * 1024 * 1024) {
      setUploadError("File exceeds 500MB.");
      return;
    }
    const modId = await ensureModId();
    if (!modId) return;
    setUploading(true);
    setUploadProgress(0);
    const interval = window.setInterval(() => {
      setUploadProgress(p => Math.min(p + 5, 90));
    }, 250);
    const ts = Date.now();
    const safe = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
    const path = `training/${modId}/${ts}-${safe}`;
    try {
      const { error } = await supabase.storage
        .from("hq-videos")
        .upload(path, file, { contentType: file.type || undefined, upsert: false });
      if (error) {
        setUploadError(error.message);
      } else {
        setEditing(e => (e ? { ...e, video_storage_path: path } : e));
        await supabase.from("hq_training_modules").update({ video_storage_path: path }).eq("id", modId);
        setUploadProgress(100);
      }
    } catch (err: any) {
      setUploadError(err?.message ?? "Upload failed.");
    } finally {
      window.clearInterval(interval);
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Training Modules</h1>
        <button onClick={() => setEditing({ title: "", required_for_roles: [], active: true })} className="btn">+ Module</button>
      </div>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2"><th className="py-2">Title</th><th>Category</th><th>Required for</th><th>Due / Recurs</th><th>Est min</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-line/70">
                <td className="py-2 font-medium">{r.title}{!r.active && <span className="ml-2 pill pill-bad">inactive</span>}</td>
                <td className="py-2 text-gray2">{r.category ?? "—"}</td>
                <td className="py-2 text-xs text-gray2">{(r.required_for_roles ?? []).length ? (r.required_for_roles ?? []).join(", ") : <span className="italic">elective</span>}</td>
                <td className="py-2 text-gray2 text-xs">
                  {r.due_within_days ? <div>Due {r.due_within_days}d of hire</div> : null}
                  {r.recurrence_months ? <div>Recerts {r.recurrence_months} mo</div> : null}
                  {!r.due_within_days && !r.recurrence_months && "—"}
                </td>
                <td className="py-2 text-gray2">{r.estimated_minutes ?? "—"}</td>
                <td className="py-2 text-right"><button onClick={() => setEditing(r)} className="btn btn-sm btn-ghost">Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[90vh] w-full max-w-xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold">{editing.id ? "Edit" : "New"} module</h2>
            <div className="space-y-3">
              <div><label className="label">Title</label><input className="input" value={editing.title ?? ""} onChange={e => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><label className="label">Description</label><textarea className="input" rows={2} value={editing.description ?? ""} onChange={e => setEditing({ ...editing, description: e.target.value || null })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Category</label><input className="input" value={editing.category ?? ""} onChange={e => setEditing({ ...editing, category: e.target.value || null })} placeholder="Safety, Compliance, Skills…" /></div>
                <div><label className="label">Sort order</label><input type="number" className="input" value={editing.sort_order ?? ""} onChange={e => setEditing({ ...editing, sort_order: e.target.value ? Number(e.target.value) : null })} placeholder="0" /></div>
              </div>
              <div><label className="label">Material URL</label><input className="input" value={editing.document_url ?? ""} onChange={e => setEditing({ ...editing, document_url: e.target.value || null })} /></div>

              <div>
                <label className="label">Video link (Loom, YouTube, Vimeo)</label>
                <div className="flex items-center gap-2">
                  <input
                    className="input flex-1"
                    placeholder="https://www.loom.com/share/… or https://youtu.be/…"
                    value={editing.video_url ?? ""}
                    onChange={e => setEditing({ ...editing, video_url: e.target.value || null })}
                  />
                  {editing.video_url && (
                    <button type="button" onClick={() => setEditing({ ...editing, video_url: null })} className="btn btn-sm btn-ghost">× remove</button>
                  )}
                </div>
              </div>

              <div>
                <label className="label">Or upload video file (mp4/mov/webm/m4v, up to 500MB)</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp4,.mov,.webm,.m4v,video/mp4,video/quicktime,video/webm,video/x-m4v"
                    className="block w-full text-sm text-gray2 file:mr-3 file:rounded file:border-0 file:bg-teal-dark file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-teal"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) void handleVideoUpload(f);
                    }}
                    disabled={uploading}
                  />
                  {editing.video_storage_path && (
                    <button type="button" onClick={() => setEditing({ ...editing, video_storage_path: null })} className="btn btn-sm btn-ghost">× remove</button>
                  )}
                </div>
                {editing.video_storage_path && (
                  <div className="mt-1 break-all text-xs text-gray2">Uploaded: {editing.video_storage_path}</div>
                )}
                {uploading && (
                  <div className="mt-2">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-line">
                      <div className="h-full bg-teal-dark transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <div className="mt-1 text-xs text-gray2">Uploading… {uploadProgress}%</div>
                  </div>
                )}
                {uploadError && <div className="mt-1 text-xs text-bad">{uploadError}</div>}
              </div>

              <div>
                <label className="label">Estimated minutes</label>
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={editing.estimated_minutes ?? ""}
                  onChange={e => setEditing({ ...editing, estimated_minutes: e.target.value ? Number(e.target.value) : null })}
                />
              </div>

              <div>
                <label className="label">Body content</label>
                <textarea className="input min-h-[120px]" value={editing.content ?? ""} onChange={e => setEditing({ ...editing, content: e.target.value || null })} />
              </div>

              <div>
                <label className="label">Required for roles</label>
                <div className="flex flex-wrap gap-3 text-sm">
                  {["employee","admin","accountant","super_admin"].map(r => {
                    const checked = (editing.required_for_roles ?? []).includes(r);
                    return <label key={r} className="flex items-center gap-1"><input type="checkbox" checked={checked} onChange={e => {
                      const cur = editing.required_for_roles ?? [];
                      setEditing({ ...editing, required_for_roles: e.target.checked ? [...cur, r] : cur.filter(x => x !== r) });
                    }} /> {r}</label>;
                  })}
                </div>
                <p className="text-xs text-gray2 mt-1">Leave all unchecked for optional / elective.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Due within N days of hire</label>
                  <input type="number" className="input" value={editing.due_within_days ?? ""} onChange={e => setEditing({ ...editing, due_within_days: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 30" />
                </div>
                <div>
                  <label className="label">Recertify every N months</label>
                  <input type="number" className="input" value={editing.recurrence_months ?? ""} onChange={e => setEditing({ ...editing, recurrence_months: e.target.value ? Number(e.target.value) : null })} placeholder="12 = annual" />
                </div>
              </div>
              <div>
                <label className="label">Prerequisite module</label>
                <select className="input" value={editing.prerequisite_module_id ?? ""} onChange={e => setEditing({ ...editing, prerequisite_module_id: e.target.value || null })}>
                  <option value="">— None —</option>
                  {rows.filter(r => r.id !== editing.id).map(r => (
                    <option key={r.id} value={r.id}>{r.title}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active ?? true} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
              <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn btn-ghost">Cancel</button><button onClick={save} className="btn" disabled={!editing.title || uploading}>Save</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 1:1 NOTES ─────────────────────────────────────────────────────────────
interface OneOnOne { id: string; employee_id: string; manager_id: string; meeting_date: string; topics: string[] | null; discussion_notes: string | null; action_items: string[] | null; feedback: string | null; performance_rating: string | null; created_at: string; }

export function OneOnOnes() {
  const { employee } = useAuth();
  const [emps, setEmps] = useState<Person[]>([]);
  const [rows, setRows] = useState<OneOnOne[]>([]);
  const [empFilter, setEmpFilter] = useState<string>("");
  const [editing, setEditing] = useState<Partial<OneOnOne> | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // HQ-HR-001: the page was readable AND writable by anyone who typed the URL,
  // so an employee could file their own 1:1 and rate themselves. Reading stays
  // open — you are meant to see your own history — but writing needs the
  // reviews permission, and the database checks the org chart on top of this
  // (sql/2026-12-22_hr_record_authorization.sql).
  const canManage = hasPermission(employee?.role, employee?.extra_permissions ?? null, "hq.reviews.write");

  useEffect(() => { if (employee) void load(); }, [employee?.id]);
  async function load() {
    if (!employee) return;
    const [{ data: e }, { data: r }] = await Promise.all([
      supabase.from("hq_employees").select("id,first_name,last_name,email").eq("status", "active").order("first_name"),
      supabase.from("hq_one_on_ones").select("*").order("meeting_date", { ascending: false }),
    ]);
    setEmps((e as Person[]) ?? []); setRows((r as OneOnOne[]) ?? []);
  }

  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);
  const visible = empFilter ? rows.filter(r => r.employee_id === empFilter) : rows;

  async function save() {
    if (!editing?.employee_id || !editing.meeting_date || !employee || !canManage) return;
    setSaveError(null);
    const payload = {
      employee_id: editing.employee_id, manager_id: employee.id,
      meeting_date: editing.meeting_date,
      topics: editing.topics ?? null,
      discussion_notes: editing.discussion_notes ?? null,
      action_items: editing.action_items ?? null,
      feedback: editing.feedback ?? null,
      performance_rating: editing.performance_rating ?? null,
    };
    // The save used to discard its error, so a row the database refused looked
    // exactly like a row it accepted (HQ-ERROR-001). It matters more now that
    // the policy can legitimately say no — writing about someone who does not
    // report to you.
    const { error } = editing.id
      ? await supabase.from("hq_one_on_ones").update(payload).eq("id", editing.id)
      : await supabase.from("hq_one_on_ones").insert(payload);
    if (error) {
      setSaveError(
        /row-level security|permission/i.test(error.message)
          ? "You can only write 1:1 notes for people who report to you."
          : error.message,
      );
      return;
    }
    setEditing(null); await load();
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">1:1 Meeting Notes</h1>
        {canManage && <button onClick={() => setEditing({ meeting_date: new Date().toISOString().slice(0, 10) })} className="btn">+ New 1:1</button>}
      </div>
      <select className="input max-w-sm" value={empFilter} onChange={e => setEmpFilter(e.target.value)}>
        <option value="">All employees</option>
        {emps.map(e => <option key={e.id} value={e.id}>{e.first_name ?? e.email} {e.last_name ?? ""}</option>)}
      </select>

      <div className="space-y-3">
        {visible.map(r => {
          const e = empById[r.employee_id];
          return (
            <div key={r.id} className="card">
              <div className="mb-2 flex items-start justify-between">
                <div>
                  <h2 className="font-semibold">{e ? (e.first_name ?? e.email) : "—"} · {fmtDay(r.meeting_date)}</h2>
                  {r.performance_rating && <span className="pill pill-mint">{r.performance_rating}</span>}
                </div>
                {canManage && <button onClick={() => setEditing(r)} className="btn btn-sm btn-ghost">Edit</button>}
              </div>
              {r.topics && r.topics.length > 0 && <div className="mb-2"><div className="text-xs uppercase text-gray2 mb-1">Topics</div><div className="flex flex-wrap gap-1">{r.topics.map((t, i) => <span key={i} className="pill pill-mint">{t}</span>)}</div></div>}
              {r.discussion_notes && <div className="mb-2 text-sm whitespace-pre-wrap">{r.discussion_notes}</div>}
              {r.action_items && r.action_items.length > 0 && <div><div className="text-xs uppercase text-gray2">Action items</div><ul className="list-disc pl-5 text-sm">{r.action_items.map((a, i) => <li key={i}>{a}</li>)}</ul></div>}
            </div>
          );
        })}
        {visible.length === 0 && <div className="card py-10 text-center text-sm text-gray2">No 1:1 notes yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold">{editing.id ? "Edit" : "New"} 1:1 notes</h2>
            {saveError && <div className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{saveError}</div>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Employee</label><select className="input" value={editing.employee_id ?? ""} onChange={e => setEditing({ ...editing, employee_id: e.target.value })}><option value="">— Select —</option>{emps.map(e => <option key={e.id} value={e.id}>{e.first_name ?? e.email}</option>)}</select></div>
                <div><label className="label">Meeting date</label><input type="date" className="input" value={editing.meeting_date ?? ""} onChange={e => setEditing({ ...editing, meeting_date: e.target.value })} /></div>
              </div>
              <div><label className="label">Topics (comma-separated)</label><input className="input" value={(editing.topics ?? []).join(", ")} onChange={e => setEditing({ ...editing, topics: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })} /></div>
              <div><label className="label">Discussion notes</label><textarea className="input min-h-[120px]" value={editing.discussion_notes ?? ""} onChange={e => setEditing({ ...editing, discussion_notes: e.target.value || null })} /></div>
              <div><label className="label">Action items (one per line)</label><textarea className="input" rows={4} value={(editing.action_items ?? []).join("\n")} onChange={e => setEditing({ ...editing, action_items: e.target.value.split("\n").map(a => a.trim()).filter(Boolean) })} /></div>
              <div><label className="label">Feedback</label><textarea className="input" rows={2} value={editing.feedback ?? ""} onChange={e => setEditing({ ...editing, feedback: e.target.value || null })} /></div>
              <div><label className="label">Performance rating</label><select className="input" value={editing.performance_rating ?? ""} onChange={e => setEditing({ ...editing, performance_rating: e.target.value || null })}><option value="">—</option><option value="exceeds">Exceeds</option><option value="meets">Meets</option><option value="developing">Developing</option><option value="needs_improvement">Needs improvement</option></select></div>
              <div className="flex justify-end gap-2"><button onClick={() => { setEditing(null); setSaveError(null); }} className="btn btn-ghost">Cancel</button><button onClick={save} className="btn" disabled={!editing.employee_id || !editing.meeting_date}>Save</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── GOALS ─────────────────────────────────────────────────────────────────
interface Goal { id: string; employee_id: string; title: string; description: string | null; metric_name: string | null; target_value: number | null; current_value: number; unit: string | null; period: string; period_start: string; period_end: string; status: string; }

export function Goals() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<Goal[]>([]);
  const [editing, setEditing] = useState<Partial<Goal> | null>(null);

  useEffect(() => { if (employee) void load(); }, [employee?.id]);
  async function load() {
    if (!employee) return;
    const { data } = await supabase.from("hq_goals").select("*").eq("employee_id", employee.id).order("period_end", { ascending: false });
    setRows((data as Goal[]) ?? []);
  }
  async function save() {
    if (!editing?.title || !editing.period_start || !editing.period_end || !employee) return;
    const payload = {
      employee_id: employee.id, title: editing.title, description: editing.description ?? null,
      metric_name: editing.metric_name ?? null, target_value: editing.target_value ?? null,
      current_value: editing.current_value ?? 0, unit: editing.unit ?? null,
      period: editing.period ?? "quarter", period_start: editing.period_start,
      period_end: editing.period_end, status: editing.status ?? "in_progress",
    };
    if (editing.id) await supabase.from("hq_goals").update(payload).eq("id", editing.id);
    else await supabase.from("hq_goals").insert(payload);
    setEditing(null); await load();
  }
  async function updateProgress(g: Goal, current: number) {
    await supabase.from("hq_goals").update({ current_value: current }).eq("id", g.id);
    await load();
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">My Goals 🎯</h1>
        <button onClick={() => setEditing({ period: "quarter", period_start: new Date().toISOString().slice(0,10), period_end: new Date(Date.now() + 90 * 86400000).toISOString().slice(0,10) })} className="btn">+ Goal</button>
      </div>

      <div className="space-y-3">
        {rows.map(g => {
          const pct = g.target_value ? Math.min(100, Math.round((Number(g.current_value) / Number(g.target_value)) * 100)) : 0;
          return (
            <div key={g.id} className="card">
              <div className="mb-2 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{g.title}</h2>
                  {g.description && <p className="text-sm text-gray2">{g.description}</p>}
                  <div className="text-xs text-gray2 mt-1">{fmtDay(g.period_start)} – {fmtDay(g.period_end)}</div>
                </div>
                <button onClick={() => setEditing(g)} className="btn btn-sm btn-ghost">Edit</button>
              </div>
              {g.target_value && (
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>{g.metric_name ?? "Progress"}</span>
                    <span className="font-semibold">{g.current_value} / {g.target_value} {g.unit}</span>
                  </div>
                  <div className="h-3 rounded-full bg-line overflow-hidden"><div className="h-full bg-teal-dark" style={{ width: `${pct}%` }} /></div>
                  <div className="mt-2 flex items-center gap-2">
                    <input type="number" className="input flex-1 py-1" placeholder="Update current value" onKeyDown={e => { if (e.key === "Enter") updateProgress(g, Number((e.target as HTMLInputElement).value)); }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <div className="card py-10 text-center text-sm text-gray2">No goals yet. Set one to track your progress.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold">{editing.id ? "Edit" : "New"} goal</h2>
            <div className="space-y-3">
              <div><label className="label">Title</label><input className="input" value={editing.title ?? ""} onChange={e => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><label className="label">Description</label><textarea className="input" rows={2} value={editing.description ?? ""} onChange={e => setEditing({ ...editing, description: e.target.value || null })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Metric</label><input className="input" value={editing.metric_name ?? ""} onChange={e => setEditing({ ...editing, metric_name: e.target.value || null })} placeholder="Sales calls" /></div>
                <div><label className="label">Unit</label><input className="input" value={editing.unit ?? ""} onChange={e => setEditing({ ...editing, unit: e.target.value || null })} placeholder="calls, $, %" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Target</label><input type="number" step="0.01" className="input" value={editing.target_value ?? ""} onChange={e => setEditing({ ...editing, target_value: e.target.value ? Number(e.target.value) : null })} /></div>
                <div><label className="label">Current</label><input type="number" step="0.01" className="input" value={editing.current_value ?? 0} onChange={e => setEditing({ ...editing, current_value: Number(e.target.value) })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Start</label><input type="date" className="input" value={editing.period_start ?? ""} onChange={e => setEditing({ ...editing, period_start: e.target.value })} /></div>
                <div><label className="label">End</label><input type="date" className="input" value={editing.period_end ?? ""} onChange={e => setEditing({ ...editing, period_end: e.target.value })} /></div>
              </div>
              <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className="btn btn-ghost">Cancel</button><button onClick={save} className="btn" disabled={!editing.title}>Save</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
